import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import "../readable-form.scss";

const TIER_OPTIONS = ["Tier 1", "Tier 2", "Tier 3"].map((v) => ({ value: v, label: v }));
const CRITICALITY_OPTIONS = ["High", "Medium", "Low"].map((v) => ({ value: v, label: v }));
const RISK_OPTIONS = ["Very High", "High", "Medium", "Low"].map((v) => ({ value: v, label: v }));

// Portal the menu out of the Bootstrap modal so it isn't clipped and the search
// input stays interactive — same fix used in DevelopmentPlanModal.
const menuPortal: any = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  menuPosition: "fixed",
  styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
};

// Same label shape as the Project Repository BU Head select
const employeeLabel = (e: any) => {
  if (!e) return "";
  const parts = [e.candidateName, e.middleName, e.lastname].filter(Boolean).join(" ").trim();
  return e.employeeId ? `${parts}-${e.employeeId}` : parts;
};

const AddOrEditCriticalRole = (props: any) => {
  const { editData, setShowModal, setEditData, handleApiCall } = props;
  const dispatch = useDispatch();
  const isEdit = !!editData;

  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [designationOptions, setDesignationOptions] = useState<any[]>([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [resolvingBuHead, setResolvingBuHead] = useState(false);
  const [form, setForm] = useState<any>({
    clientRef: editData?.clientRef ?? "",
    clientLabel: editData?.client?.clientName ?? "",
    designationRef: editData?.designationRef ?? "",
    designationLabel: editData?.designation?.title ?? "",
    tier: editData?.tier ?? "",
    criticality: editData?.criticality ?? "",
    riskLevel: editData?.riskLevel ?? "",
    reason: editData?.reason ?? "",
    buHeadRef: editData?.buHeadRef ?? "",
    buHeadLabel: editData?.buHead ? employeeLabel(editData.buHead) : "",
  });
  const [errors, setErrors] = useState<any>({ clientRef: "", designationRef: "", buHeadRef: "" });
  const [btnDisable, setBtnDisable] = useState(false);

  // Full employee list — same source the Project Repository form uses for BU Head.
  const employeeData = useSelector((state: any) => state.employeeReducer.getEmployeeData);
  const buHeadOptions: any[] = (employeeData?.data || []).map((e: any) => ({
    value: e._id,
    label: employeeLabel(e),
  }));
  useEffect(() => {
    dispatch(actions.getEmployeeDataAction());
  }, []);

  // Load clients that actually have employees (not the full client master)
  useEffect(() => {
    if (!isEdit) {
      dispatch(
        actions.getClientsWithEmployeesAction((res: any) => {
          if (res?.data) {
            setClientOptions(
              res.data.map((c: any) => ({
                value: c._id,
                label: `${c.clientName} (${c.employeeCount})`,
                name: c.clientName,
              }))
            );
          }
        })
      );
    }
  }, [isEdit]);

  // Cascade: when the client changes, load only that client's designations and
  // re-resolve BU Head. Changing client always overwrites BU Head (the previous
  // value belonged to the previous client), so any manual override is discarded.
  const handleClientChange = (sel: any) => {
    const clientRef = sel?.value || "";
    setForm({
      ...form,
      clientRef,
      clientLabel: sel?.label || "",
      designationRef: "",
      designationLabel: "",
      buHeadRef: "",
      buHeadLabel: "",
    });
    setDesignationOptions([]);
    if (!clientRef) return;
    setLoadingDesignations(true);
    dispatch(
      actions.getDesignationsByClientAction(clientRef, (res: any) => {
        setLoadingDesignations(false);
        if (res?.data) {
          setDesignationOptions(
            res.data.map((d: any) => ({
              value: d._id,
              label: `${d.title} (${d.employeeCount})`,
              name: d.title,
            }))
          );
        }
      })
    );
    // Auto-fill BU Head from the client's projects (editable afterwards)
    setResolvingBuHead(true);
    dispatch(
      actions.resolveBuHeadByClientAction(clientRef, (res: any) => {
        setResolvingBuHead(false);
        const bh = res?.data;
        if (bh?.buHeadRef) {
          setForm((prev: any) => ({
            ...prev,
            buHeadRef: bh.buHeadRef,
            buHeadLabel: employeeLabel(bh.buHead) || bh.buHeadRef,
          }));
        }
      })
    );
  };

  const validate = () => {
    if (!form.clientRef) {
      setErrors({ clientRef: "Please select a client", designationRef: "", buHeadRef: "" });
      return false;
    }
    if (!form.designationRef) {
      setErrors({ clientRef: "", designationRef: "Please select a designation", buHeadRef: "" });
      return false;
    }
    if (!form.buHeadRef) {
      setErrors({ clientRef: "", designationRef: "", buHeadRef: "Please select a BU head" });
      return false;
    }
    setErrors({ clientRef: "", designationRef: "", buHeadRef: "" });
    return true;
  };

  const done = (res: any, msg: string) => {
    setBtnDisable(false);
    if (res?.error) {
      const m =
        res?.error?.response?.data?.error?.error_description || "Something went wrong";
      showErrorToastMessage(m);
    } else if (res?.data) {
      showSuccessToastMessage(msg);
      setShowModal(false);
      setEditData("");
      handleApiCall();
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (!validate()) return;
    setBtnDisable(true);
    const payload: any = {
      tier: form.tier,
      criticality: form.criticality,
      riskLevel: form.riskLevel,
      reason: form.reason?.trim(),
      buHeadRef: form.buHeadRef,
    };
    if (isEdit) {
      dispatch(
        actions.updateCriticalRoleAction(editData._id, payload, (res: any) =>
          done(res, "Critical role updated")
        )
      );
    } else {
      payload.designationRef = form.designationRef;
      payload.clientRef = form.clientRef;
      dispatch(
        actions.createCriticalRoleAction(payload, (res: any) =>
          done(res, "Critical role identified")
        )
      );
    }
  };

  return (
    <div className="readable-form">
      <div>
        <label className="form-label">Client<span className="required">*</span></label>
        {isEdit ? (
          <input type="text" className="form-control" value={form.clientLabel} disabled />
        ) : (
          <Select
            className="dropdown-select"
            isSearchable={true}
            placeholder="Select client"
            value={form.clientRef ? { value: form.clientRef, label: form.clientLabel } : null}
            options={clientOptions}
            onChange={handleClientChange}
            {...menuPortal}
          />
        )}
        {errors.clientRef !== "" && <p className="error">{errors.clientRef}</p>}
      </div>

      <div className="mt-2">
        <label className="form-label">Designation<span className="required">*</span></label>
        {isEdit ? (
          <input type="text" className="form-control" value={form.designationLabel} disabled />
        ) : (
          <Select
            className="dropdown-select"
            isSearchable={true}
            isDisabled={!form.clientRef || loadingDesignations}
            placeholder={
              !form.clientRef
                ? "Select a client first"
                : loadingDesignations
                ? "Loading…"
                : "Select designation"
            }
            value={form.designationRef ? { value: form.designationRef, label: form.designationLabel } : null}
            options={designationOptions}
            noOptionsMessage={() => "No designations found for this client"}
            onChange={(sel: any) =>
              setForm({ ...form, designationRef: sel?.value || "", designationLabel: sel?.label || "" })
            }
            {...menuPortal}
          />
        )}
        {errors.designationRef !== "" && <p className="error">{errors.designationRef}</p>}
      </div>

      <div className="mt-2">
        <label className="form-label">BU Head<span className="required">*</span></label>
        <Select
          className="dropdown-select"
          isSearchable={true}
          placeholder={resolvingBuHead ? "Finding BU head…" : "Select BU head"}
          value={form.buHeadRef ? { value: form.buHeadRef, label: form.buHeadLabel } : null}
          options={buHeadOptions}
          onChange={(sel: any) =>
            setForm({ ...form, buHeadRef: sel?.value || "", buHeadLabel: sel?.label || "" })
          }
          {...menuPortal}
        />
        {errors.buHeadRef !== "" && <p className="error">{errors.buHeadRef}</p>}
      </div>

      <div className="mt-2">
        <label className="form-label">Tier</label>
        <Select
          className="dropdown-select"
          placeholder="Select tier"
          value={form.tier ? { value: form.tier, label: form.tier } : null}
          options={TIER_OPTIONS}
          onChange={(sel: any) => setForm({ ...form, tier: sel?.value || "" })}
        />
      </div>

      <div className="mt-2">
        <label className="form-label">Criticality</label>
        <Select
          className="dropdown-select"
          placeholder="Select criticality"
          value={form.criticality ? { value: form.criticality, label: form.criticality } : null}
          options={CRITICALITY_OPTIONS}
          onChange={(sel: any) => setForm({ ...form, criticality: sel?.value || "" })}
        />
      </div>

      <div className="mt-2">
        <label className="form-label">Risk Level</label>
        <Select
          className="dropdown-select"
          placeholder="Select risk level"
          value={form.riskLevel ? { value: form.riskLevel, label: form.riskLevel } : null}
          options={RISK_OPTIONS}
          onChange={(sel: any) => setForm({ ...form, riskLevel: sel?.value || "" })}
        />
      </div>

      <div className="mt-2">
        <label className="form-label">Reason for Criticality</label>
        <textarea
          className="form-control"
          rows={2}
          placeholder="e.g. 24/7 operations, single point of failure"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
      </div>

      <div className="modal-btn-wrapper mt-4 mb-4">
        <div>
          <button
            className="btn cancel-btn buttonsize"
            onClick={() => {
              setEditData("");
              setShowModal(false);
            }}
          >
            Cancel
          </button>
        </div>
        <div>
          <button
            className="btn add-submit-button buttonsize"
            disabled={btnDisable}
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddOrEditCriticalRole;
